import { useCachedResource, useActiveOrg } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { shows } from "../local/db";

// Shows the caller's org can see (score-api scopes by the token's org). The
// scribe picks one on /setup. Read-only; shows are FVD-owned (card #379).
// Cache-first (#421): served from IndexedDB, revalidated from score-api online.
//
// #1736 — the cached read is scoped to the ACTIVE org. The IndexedDB store is
// cross-org (shows.putAll only upserts, so every org the device has loaded
// stays in it), so an unscoped read served the previous org's shows after a
// switch: selecting a different org left the old org's shows on screen, and a
// scribe could pick a show outside the active org entirely. The remote list is
// already org-scoped by the X-Org-Id header, so only the local read needed
// fixing. `activeOrgId` is a dep so a switch re-runs the whole cache-first pass.
//
// #1746 — #1736 shipped no DB migration and did not bump DB_VERSION, so a
// device that cached shows before v0.29.1 has rows with no orgId at all,
// which shows.byOrg's filter can never match. That's invisible online
// (putAll re-stamps orgId on the next fetch), but offline it left such a
// device's show picker with zero options: a regression against pre-#1736
// behaviour. Fixed with a read-time fallback inside shows.byOrg, not a
// DB_VERSION migration: a migration would have to stamp a multi-org
// device's already-accumulated cross-org pile of legacy rows with a single
// org id, mislabeling every other org's rows in that pile permanently. The
// read-time fallback accepts a narrower, self-healing version of the same
// cost instead: on a device that cached MORE THAN ONE org's shows before
// v0.29.1, this fallback cannot tell those orgs' unstamped rows apart, so
// it can serve one org's pre-v0.29.1 shows under another org until the
// READING org gets an online fetch of its own, or every other org holding
// legacy rows gets one (with three or more such orgs on a device, one other
// org fetching only clears that org's rows, not the rest of the pile). See
// the byOrg doc comment in local/db.js for the fallback itself and the full
// accounting of that window.
export function useShows() {
  const api = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const { online } = useScoreContext();
  return useCachedResource(
    async () => {
      // With no active org there is nothing safe to serve from cache: the API
      // falls back to the token's first org, and the cache cannot know which
      // that is. Report "nothing cached" and let the network answer rather
      // than guessing with another org's rows.
      if (!activeOrgId) return undefined;
      const cached = await shows.byOrg(activeOrgId);
      return cached.length ? cached : undefined;
    },
    async () => {
      const list = unwrap(await api.shows.listShows());
      await shows.putAll(list);
      return list;
    },
    [api, activeOrgId],
    online,
  );
}
